import React, { useMemo } from 'react';

import { usePsfSharePageData } from 'src/app/pages/psf/share/pageContext';
import { usePsfShareAvailableShareActions } from 'src/app/pages/psf/share/shared/hooks';
import { useDeviceMessagingIcon } from 'src/app/shared/hooks/share';
import { useIsMobile } from 'src/app/shared/hooks/userAgent';

import { mapButtonConfigs, type ShareActionItem } from './mapButtonConfigs';
import type { ShareChannel } from './types';

export type { ShareChannel };

type UseActionItemsResult = ModelHookResult<{
	first: ShareActionItem | undefined;
	rest: readonly ShareActionItem[];
	mobileShareVisual: string | undefined;
}>;

export function useShareActions(): UseActionItemsResult {
	const pageData = usePsfSharePageData();
	const { bestShareVisual } = pageData.data.petition;
	const { isShareAssetsEnabled } = pageData.data.featureConfigs;
	const isMobile = useIsMobile();
	const { MessagingIconComponent, variant: smsVariant } = useDeviceMessagingIcon();

	const availableShareActions = usePsfShareAvailableShareActions();
	const buttonConfigs = useMemo(
		() =>
			mapButtonConfigs(availableShareActions, {
				sms: { icon: React.createElement(MessagingIconComponent), variant: smsVariant },
			}),
		[availableShareActions, MessagingIconComponent, smsVariant],
	);

	const shareVisualUrl = bestShareVisual?.url;
	const mobileShareVisual = isShareAssetsEnabled && isMobile ? shareVisualUrl : undefined;

	if (buttonConfigs.length === 0) {
		return { data: { first: undefined, rest: [], mobileShareVisual } };
	}

	const [firstItem, ...restItems] = buttonConfigs;

	if (firstItem.channel !== 'copy') {
		return { data: { first: undefined, rest: buttonConfigs, mobileShareVisual } };
	}

	return { data: { first: firstItem, rest: restItems, mobileShareVisual } };
}
